import { fieldToOptionsNotVarDeleted } from 'services/helpers';
import {
  checkListDeleted,
  checkDeleted,
  displayNameDescriptor,
} from 'pages/AutomationEngine/helpers';
import { initElement, initState } from './index';
import {
  MATH_OPERATOR_STEP,
  SIMPLE_BUILDER_TYPES,
  ELEMENT_TYPES,
  SIMPLE_BUILDER_OPERATION_TYPES,
} from './constants';

import {
  BuilderSimpleSubTypes,
  BuilderType,
  SimpleBuilderArgument,
  TargetFieldVariable,
} from './types';

import { monitoringMessageHelper } from 'instrumentation/helpers';

import {
  AddErrorMessage,
  MessageDictionary,
} from 'pages/AutomationEngine/messageDictionary';
import { GenericOption } from 'pages/AutomationEngine/types';

type ApiVariable = { name: string; deleted: boolean };

const checkElements = (
  messageDictionary: MessageDictionary,
  addErrorMessage: AddErrorMessage,
  mathArgs: SimpleBuilderArgument[]
) => {
  const fields = mathArgs
    .filter(({ argumentType }) => argumentType === ELEMENT_TYPES.field)
    .map(({ field }) => field);

  checkListDeleted(
    messageDictionary,
    addErrorMessage,
    fields,
    displayNameDescriptor
  );
};

const apiVariableToOption = (variable: ApiVariable) => ({
  ...variable,
  label: variable.name,
  value: variable.name,
  isVariable: true,
  deleted: false,
});

const getSimpleData = (
  _type: any,
  subType: string,
  fieldData: (GenericOption & { isVariable: true }) | null,
  variable: ApiVariable | null,
  simpleBuilderArguments: SimpleBuilderArgument[],
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary
) => {
  // TODO for now we only handle type === 'simple_builder'
  const { simpleData } = initState;
  checkDeleted(
    messageDictionary,
    addErrorMessage,
    fieldData,
    displayNameDescriptor
  );
  checkElements(messageDictionary, addErrorMessage, simpleBuilderArguments);

  switch (subType) {
    case SIMPLE_BUILDER_TYPES.addSubtract:
      return {
        ...simpleData,
        [SIMPLE_BUILDER_TYPES.addSubtract]: {
          targetField: fieldData
            ? fieldToOptionsNotVarDeleted(fieldData)
            : variable
              ? apiVariableToOption(variable)
              : null,
          elements: simpleBuilderArguments.map(
            ({ argumentType, constant, field, variable, operationType }) =>
              initElement(
                argumentType,
                argumentType === ELEMENT_TYPES.field
                  ? fieldToOptionsNotVarDeleted(field)
                  : argumentType === ELEMENT_TYPES.variable
                    ? apiVariableToOption(variable)
                    : constant,
                operationType
              )
          ),
        },
      };

    case SIMPLE_BUILDER_TYPES.multiplicationDivision: {
      let elements = simpleBuilderArguments.map(
        ({ argumentType, constant, field, variable, operationType }) =>
          initElement(
            argumentType,
            argumentType === ELEMENT_TYPES.field
              ? fieldToOptionsNotVarDeleted(field)
              : argumentType === ELEMENT_TYPES.variable
                ? apiVariableToOption(variable)
                : constant,
            operationType
          )
      );

      // try and find the divide element
      const index = elements.findIndex(
        ({ operationType }) =>
          operationType === SIMPLE_BUILDER_OPERATION_TYPES.divide.value
      );

      // set them all back to 'regular'
      elements = elements.map(({ operationType, ...rest }) => ({
        ...rest,
        operationType: SIMPLE_BUILDER_OPERATION_TYPES.add.value,
      }));
      if (index !== -1) {
        return {
          ...simpleData,
          [SIMPLE_BUILDER_TYPES.multiplicationDivision]: {
            targetField: fieldData
              ? fieldToOptionsNotVarDeleted(fieldData)
              : variable
                ? apiVariableToOption(variable)
                : null,
            numeratorElements: elements.slice(0, index),
            denominatorElements: elements.slice(index),
          },
        };
      }
      // all the data is above the line
      return {
        ...simpleData,
        [SIMPLE_BUILDER_TYPES.multiplicationDivision]: {
          targetField: fieldData
            ? fieldToOptionsNotVarDeleted(fieldData)
            : variable
              ? apiVariableToOption(variable)
              : null,
          numeratorElements: elements,
          denominatorElements: [],
        },
      };
    }
    default:
      monitoringMessageHelper('unknown subtype', {
        extra: {
          subType,
        },
      });
      return simpleData;
  }
};

type ApiObject = {
  type: BuilderType;
  subtype: BuilderSimpleSubTypes;
  field: (GenericOption & { isVariable: true }) | null;
  variable: { name: string; deleted: boolean };
  simpleBuilderArguments: SimpleBuilderArgument[];
};

const forFlow = (
  apiObject: ApiObject,
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary
) => {
  const { type, subtype, field, variable, simpleBuilderArguments } = apiObject;

  const details = {
    ...initState,

    builderType: type,
    subType: subtype,
    step: MATH_OPERATOR_STEP.chooseSimpleType,
    simpleData: getSimpleData(
      type,
      subtype,
      field,
      variable,
      simpleBuilderArguments,
      addErrorMessage,
      messageDictionary
    ),
  };

  return details;
};

type MapElementsForApi = Pick<
  SimpleBuilderArgument,
  'operationType' | 'argumentType' | 'value'
>;

const mapElementsForApi = ({
  operationType,
  argumentType,
  value,
}: MapElementsForApi) => {
  return {
    argumentType: argumentType,
    operationType,
    constant: argumentType === ELEMENT_TYPES.constant ? value : null, // we can pass null to the constant
    ...(argumentType === ELEMENT_TYPES.field
      ? { fieldId: (value as GenericOption & { isVariable: boolean })!.value }
      : null), // but not the field_id
    ...(argumentType === ELEMENT_TYPES.variable
      ? { variable: { name: (value as TargetFieldVariable).value } }
      : null), // or variable
  };
};

const forApi = (step: any) => {
  let field_id = null;
  let variable = null;
  let fieldOrVariable = {};
  const details = { ...step.details };
  const { builderType: type, subType: subtype } = details;

  if (!details.simpleData[subtype].targetField?.isVariable) {
    field_id = details.simpleData[subtype].targetField.value;
    fieldOrVariable = { field_id };
  } else {
    variable = { name: details.simpleData[subtype].targetField.value };
    fieldOrVariable = { variable };
  }

  let simpleBuilderArguments;
  if (subtype === SIMPLE_BUILDER_TYPES.addSubtract) {
    simpleBuilderArguments =
      details.simpleData[subtype].elements.map(mapElementsForApi);
  } else if (subtype === SIMPLE_BUILDER_TYPES.multiplicationDivision) {
    // msp the numerators
    const numeratorElements =
      details.simpleData[subtype].numeratorElements.map(mapElementsForApi);

    // map the denominators
    const denominatorElements =
      details.simpleData[subtype].denominatorElements.map(mapElementsForApi);
    // set the first one to divide
    if (denominatorElements.length) {
      denominatorElements[0].operationType =
        SIMPLE_BUILDER_OPERATION_TYPES.divide.value;
    }
    simpleBuilderArguments = [...numeratorElements, ...denominatorElements];
  }
  return {
    details: { type, subtype, ...fieldOrVariable, simpleBuilderArguments },
  };
};

export const mathOperatorDTO = {
  forApi,
  forFlow,
};
